/**
 * Hook que mantém o token FCM do dispositivo atualizado no backend
 * sempre que o usuário autenticado abre/foca o app.
 *
 * Por que existe:
 *   - O token FCM no iOS PWA pode rotacionar/invalidar sem aviso
 *     (ITP, reinício do dispositivo, reinstalação do PWA).
 *   - O `device_name` e `device_type` precisam refletir a origem real
 *     (PWA dev vs PWA app oficial, iOS vs macOS), o que só é possível
 *     após a chamada de `register_user_device` rodar dentro desse origin.
 *   - Ao logar no navegador, precisamos também "reivindicar a posse" da
 *     fingerprint deste browser: se o usuário anterior tinha token push
 *     ativo, o worker continuaria enviando notificações do tenant antigo
 *     para este dispositivo. A RPC `deactivate_foreign_devices_on_this_browser`
 *     desativa tokens de outros usuários com a mesma fingerprint.
 *
 * Comportamento:
 *   - Roda 1x por sessão de userId logo após o login.
 *   - Roda novamente quando o app volta ao foco (visibilitychange).
 *   - É fire-and-forget: nunca bloqueia UI, nunca mostra erro ao usuário.
 *   - Só faz refresh de token se a permissão de notificação já estiver 'granted',
 *     mas a reivindicação de posse do browser sempre roda no login.
 */
import { useEffect, useRef } from 'react';
import { clearLocalFcmTokenRegistration, silentRefreshToken, getPermissionState } from '@/lib/fcm';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useFcmTokenRefresh');

/**
 * Detecta o device_type de forma equivalente ao helper interno do fcm.ts.
 * Precisa gerar o MESMO valor que `register_user_device` usa para que a
 * fingerprint calculada pela RPC bata com a persistida em `user_devices`.
 */
function detectDeviceType(): string {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent || '';
  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && (navigator as unknown as { standalone?: boolean }).standalone === true);

  if (/iPhone|iPad|iPod/i.test(ua)) return isStandalone ? 'ios-pwa' : 'ios-web';
  if (/Android/i.test(ua)) return isStandalone ? 'android-pwa' : 'android-web';
  return isStandalone ? 'desktop-pwa' : 'web';
}

/**
 * Ao logar em um navegador, desativa registros push de qualquer OUTRO usuário
 * que compartilhe a mesma device_fingerprint — impede que o pop-up nativo do
 * tenant anterior continue caindo neste dispositivo. Fire-and-forget.
 */
async function reclaimBrowserOwnership(): Promise<number> {
  try {
    const origin = typeof window !== 'undefined' ? window.location.hostname : null;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null;
    const { data, error } = await supabase.rpc(
      'deactivate_foreign_devices_on_this_browser' as any,
      {
        p_device_type: detectDeviceType(),
        p_user_agent: ua,
        p_origin: origin,
      } as any,
    );
    if (error) {
      logger.warn('Falha ao reivindicar posse do browser', error);
      return 0;
    }
    if (typeof data === 'number' && data > 0) {
      logger.info(`Devices de outros usuários desativados nesta fingerprint: ${data}`);
      // Se havia device estrangeiro ativo neste browser/origin, o cache local
      // do token pode apontar para o usuário anterior. Limpar força re-registro
      // do token no backend para o usuário/tenant autenticado agora.
      clearLocalFcmTokenRegistration();
      return data;
    }
    return 0;
  } catch (err) {
    logger.warn('Exceção ao reivindicar posse do browser (ignorada)', err);
    return 0;
  }
}

export function useFcmTokenRefresh(userId: string | null): void {
  const lastUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      lastUserRef.current = null;
      return;
    }

    const tryRefresh = (forceRegister = false) => {
      if (getPermissionState() !== 'granted') return;
      // fire-and-forget — falhas são logadas internamente pelo logger
      void silentRefreshToken({ forceRegister });
    };

    // 1ª execução no login do usuário
    if (lastUserRef.current !== userId) {
      lastUserRef.current = userId;
      // Primeiro reivindica a posse do navegador (desativa tokens de outros
      // usuários com a mesma fingerprint), só depois tenta refresh do próprio
      // token. Assim, mesmo que o usuário atual não conceda push, o token
      // antigo do usuário anterior já foi neutralizado no backend.
      void reclaimBrowserOwnership().then(() => {
        // Em troca de usuário, sempre força o registro do token atual. Isso
        // reativa o device correto caso ele tenha sido limpo no backend e evita
        // confiar em `fcm:lastToken@origin` gravado por uma sessão anterior.
        tryRefresh(true);
      });
    }

    // Re-tenta toda vez que o PWA volta ao foco — cobre o caso
    // "abri o app depois de muito tempo" / "voltei de uma rotação de token"
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryRefresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [userId]);
}
